import React from 'react';
import { MapPin, History, ShieldAlert, TrendingUp, TrendingDown, Target, Sparkles, FileText, AlertCircle, Activity } from 'lucide-react';
import ClientLogo from './ClientLogo';
import ClientScoreTimeline from './ClientScoreTimeline';
import LoyaltyBadge from './LoyaltyBadge';
import ClientActionsPanel from './ClientActionsPanel';
import { reconstructLoyaltyTimeline, computeClientActionPlan, computeClientKPIs } from '../../utils/clientCrm';

const TONE_PRESETS = {
  urgent: {
    icon: AlertCircle,
    border: 'border-rose-500/40',
    bg: 'bg-rose-500/10',
    iconColor: 'text-rose-300',
  },
  positive: {
    icon: Sparkles,
    border: 'border-emerald-500/40',
    bg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-300',
  },
  normal: {
    icon: Target,
    border: 'border-accent/40',
    bg: 'bg-accent/10',
    iconColor: 'text-accent',
  },
};

/**
 * ClientDetailPanel — right-side panel of the /clients refactored layout.
 *
 * Renders a polished client detail view with :
 *   - Header (logo + name + segment + cooldown)
 *   - Loyalty score timeline (recharts)
 *   - 3 KPIs (active contracts / events / incidents)
 *   - Plan de relance commercial (1-3 recommendations)
 *   - Historique relationnel narratif
 */
export default function ClientDetailPanel({ client, data, tier, totalDays, activeContracts, onCooldown, cooldownLeft }) {
  const timeline = reconstructLoyaltyTimeline(data, totalDays);
  const plan = computeClientActionPlan(client, { ...data, lastCheckedDay: totalDays }, tier);
  const kpis = computeClientKPIs(client, data, activeContracts);

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid={`client-detail-${client.id}`}>
      {/* Header */}
      <div
        className="px-5 py-4 border-b border-ink-600 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${client.color}22, transparent 60%)` }}
      >
        <div className="flex items-start gap-4 relative z-10">
          <ClientLogo client={client} size="xl" withBorder className="flex-shrink-0 shadow-xl" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-display font-black text-foreground tracking-tight leading-tight">{client.name}</h2>
            <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-1">
              <MapPin className="w-3 h-3" />
              {client.city}
              <span className="text-slate-600 mx-1">·</span>
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-ink-700/60 text-slate-300 uppercase tracking-wider">{client.segment}</span>
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {(client.themes || []).map(t => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-ink-700/40 text-slate-400 border border-ink-600 font-mono">
                  {t}
                </span>
              ))}
            </div>
            {onCooldown && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-rose-900/30 border border-rose-500/40 text-rose-200" data-testid={`detail-cooldown-${client.id}`}>
                <ShieldAlert className="w-3 h-3" />
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider">Période de gel — {cooldownLeft}j restants</span>
              </div>
            )}
          </div>
          <LoyaltyBadge score={data.score} size="lg" />
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Score timeline */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" />
              Évolution de la fidélité
            </h3>
            <span className="text-[10px] font-mono text-slate-500">
              {timeline.length - 1} point{timeline.length > 2 ? 's' : ''}
            </span>
          </div>
          <ClientScoreTimeline points={timeline} brandColor={client.color} />
        </section>

        {/* KPIs */}
        <section className="grid grid-cols-3 gap-2" data-testid={`client-kpis-${client.id}`}>
          <KPI icon={FileText} label="Contrats actifs" value={kpis.contractsActive} color="text-accent" />
          <KPI icon={Activity} label="Événements" value={kpis.eventsTotal} color="text-money" />
          <KPI icon={AlertCircle} label="Incidents" value={kpis.incidents} color={kpis.incidents > 2 ? 'text-rose-300' : 'text-slate-400'} />
        </section>

        {/* Actions commerciales — 28/07/2026 (retour user prod) */}
        <ClientActionsPanel client={client} data={data} />

        {/* Action plan */}
        {plan.length > 0 && (
          <section data-testid={`client-action-plan-${client.id}`}>
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5 mb-2">
              <Target className="w-3 h-3" />
              Plan de relance commercial
            </h3>
            <div className="space-y-2">
              {plan.map((p, i) => {
                const preset = TONE_PRESETS[p.tone] || TONE_PRESETS.normal;
                const Icon = preset.icon;
                return (
                  <div
                    key={`plan-${i}-${(p.headline || '').slice(0, 24)}`}
                    className={`px-3 py-2.5 rounded-md border ${preset.border} ${preset.bg}`}
                    data-testid={`action-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${preset.iconColor}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-100">{p.headline}</p>
                        <p className="text-[11px] text-slate-300 mt-0.5 leading-relaxed">{p.detail}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Event history */}
        <section data-testid={`client-history-${client.id}`}>
          <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5 mb-2">
            <History className="w-3 h-3" />
            Historique relationnel ({(data.events || []).length})
          </h3>
          {(data.events || []).length === 0 ? (
            <p className="text-xs text-slate-500 italic py-3 text-center bg-ink-900/30 rounded border border-ink-700">
              Pas d&apos;événement enregistré. Acceptez un contrat pour démarrer la relation.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {(data.events || []).map((e, i) => (
                <li
                  key={`${e.eventId}-${e.day}-${i}`}
                  className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-ink-900/40 hover:bg-ink-900/70 transition-colors"
                >
                  {e.delta > 0
                    ? <TrendingUp className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                    : <TrendingDown className="w-3 h-3 text-rose-400 flex-shrink-0" />
                  }
                  <span className="text-slate-300 truncate">{e.label}</span>
                  {e.contractName && (
                    <span className="text-[10px] text-slate-500 italic truncate">— {e.contractName}</span>
                  )}
                  <span className={`text-[10px] font-mono font-bold ml-auto ${e.delta > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {e.delta > 0 ? `+${e.delta}` : e.delta}
                  </span>
                  <span className="text-[10px] text-slate-500 font-mono">J{e.day}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="px-3 py-2 rounded-md bg-ink-900/40 border border-ink-700">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-xl font-mono font-bold mt-1 ${color || 'text-foreground'}`}>{value}</div>
    </div>
  );
}
